import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  fmtDateTime,
  setEscalationThreshold,
  useEscalationThresholds,
  type EscalationThreshold,
} from "@/lovable-demo/lib/governance/escalations";

/**
 * Governed escalation thresholds (Escalation Trigger & Authority Matrix).
 * Values are data, not code; each change keeps old/new, who, rationale and effective date in append-only history.
 */
export function EscalationThresholds() {
  const { thresholds, changes, loading, refresh } = useEscalationThresholds();
  const [edit, setEdit] = useState<EscalationThreshold | null>(null);
  const [value, setValue] = useState("");
  const [rationale, setRationale] = useState("");
  const [effective, setEffective] = useState("");
  const [busy, setBusy] = useState(false);
  const byCat = new Map<string, EscalationThreshold[]>();
  thresholds.forEach((t) => byCat.set(t.category, [...(byCat.get(t.category) ?? []), t]));
  const submit = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      await setEscalationThreshold(
        edit.id,
        Number(value),
        rationale,
        effective ? new Date(effective).toISOString() : undefined,
      );
      toast.success("Threshold change recorded with audit history.");
      setEdit(null);
      void refresh();
    } catch (err) {
      toast.error(`Not changed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="mt-8 border-t border-border pt-6">
      <h2 className="text-base font-semibold">Escalation thresholds</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Level 1 / 2 / 3 criteria used by rule-triggered escalation. "Reference default" values are
        placeholders until the customer approves its own. Safety / regulatory conditions are an
        independent override (always Level 3) and are not configured here.
      </p>
      {loading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Category</th>
                <th>Level 1</th>
                <th>Level 2</th>
                <th>Level 3</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {[...byCat.entries()].map(([cat, list]) => (
                <tr key={cat}>
                  <td className="py-1.5 pr-2">
                    <span className="font-semibold">{cat}</span>
                    <span className="block text-muted-foreground">{list[0]?.description}</span>
                  </td>
                  {[1, 2, 3].map((l) => {
                    const t = list.find((x) => x.level === l);
                    return (
                      <td key={l} className="num">
                        {t ? (
                          <button
                            type="button"
                            className="hover:text-primary hover:underline"
                            onClick={() => {
                              setEdit(t);
                              setValue(String(t.threshold));
                              setRationale("");
                              setEffective("");
                            }}
                          >
                            ≥ {Number(t.threshold).toLocaleString("en-US")} {t.unit}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    );
                  })}
                  <td className="text-muted-foreground">
                    {list.some((x) => x.approval_status === "customer_approved")
                      ? "Customer-approved"
                      : "Reference default"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <div className="mt-4 max-w-lg space-y-2 rounded border border-border p-3">
          <p className="text-sm font-semibold">
            {edit.category} · Level {edit.level} (current ≥{" "}
            {Number(edit.threshold).toLocaleString("en-US")} {edit.unit})
          </p>
          <Input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label="New threshold"
          />
          <Input
            type="datetime-local"
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
            aria-label="Effective from"
          />
          <Textarea
            placeholder="Rationale / customer approval reference (required)"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy || rationale.trim().length < 10 || value === ""}
              onClick={submit}
            >
              {busy ? "Recording…" : "Record change"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEdit(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <details className="mt-4">
        <summary className="cursor-pointer text-xs font-semibold text-primary">
          Threshold change history ({changes.length})
        </summary>
        <ul className="mt-2 space-y-1.5 text-xs">
          {changes.length === 0 && (
            <li className="text-muted-foreground">No changes recorded yet.</li>
          )}
          {changes.map((c) => (
            <li key={c.id}>
              <span className="num text-muted-foreground">{fmtDateTime(c.created_at)}</span> ·{" "}
              {c.category} L{c.level}: {c.old_value} → {c.new_value} · effective{" "}
              {fmtDateTime(c.effective_from)} · changed by {c.changed_by_label} · approved by{" "}
              {c.approved_by_label}
              <span className="block text-foreground/80">{c.rationale}</span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
